/**
 * Utility functions for working with tooth notation
 */

import type { Quadrant, PediatricQuadrant } from '~/types/quadrant';
import { quadrantAttributes } from '~/types/quadrant';

// Mapping between letter notation and FDI numeric notation for pediatric teeth
export const LETTER_TO_NUMBER: Record<string, number> = {
  // Upper right quadrant
  A: 51,
  B: 52,
  C: 53,
  D: 54,
  E: 55,
  // Upper left quadrant
  F: 61,
  G: 62,
  H: 63,
  I: 64,
  J: 65,
  // Lower left quadrant
  K: 71,
  L: 72,
  M: 73,
  N: 74,
  O: 75,
  // Lower right quadrant
  P: 81,
  Q: 82,
  R: 83,
  S: 84,
  T: 85,

  // quadrants
  UR: 201,
  UL: 202,
  LL: 203,
  LR: 204,
};

// Reverse mapping for display purposes
export const NUMBER_TO_LETTER: Record<number, string> = Object.entries(LETTER_TO_NUMBER).reduce(
  (acc, [letter, number]) => {
    acc[number] = letter;
    return acc;
  },
  {} as Record<number, string>
);

/**
 * Converts a pediatric tooth letter notation to FDI numeric notation
 * @param letter The letter notation (A-T)
 * @returns The corresponding FDI number or 0 if invalid
 */
export function letterToNumber(letter: string): number {
  return LETTER_TO_NUMBER[letter.toUpperCase()] || 0;
}

/**
 * Converts an FDI numeric notation to pediatric letter notation
 * @param number The FDI number (51-55, 61-65, 71-75, 81-85)
 * @returns The corresponding letter notation or empty string if invalid
 */
export function numberToLetter(number: number): string {
  return NUMBER_TO_LETTER[number] || '';
}

/**
 * Checks if a tooth number is in the pediatric range
 * @param toothNumber The tooth number to check
 * @returns True if the tooth is in the pediatric range (50 - 100) or bigger than 200 (pediatric quadrants)
 */
export function isPediatricTooth(toothNumber: number): boolean {
  return (toothNumber >= 50 && toothNumber < 100) || toothNumber > 200;
}

/**
 * Converts an array of pediatric letter notations to FDI numeric notations
 * @param letters Array of letter notations
 * @returns Array of corresponding FDI numbers (invalid entries filtered out)
 */
export function lettersToNumbers(letters: string[]): number[] {
  return letters.map(letter => letterToNumber(letter)).filter(num => num > 0);
}

/**
 * Converts an array of FDI numeric notations to pediatric letter notations
 * @param numbers Array of FDI numbers
 * @returns Array of corresponding letter notations (invalid entries filtered out)
 */
export function numbersToLetters(numbers: number[]): string[] {
  return numbers
    .filter(num => isPediatricTooth(num))
    .map(num => numberToLetter(num))
    .filter(letter => letter !== '');
}

export const formatToothOrArea = (tooth: number): string => {
  return quadrantAttributes[tooth as Quadrant]?.description ?? `#${tooth}`;
};

/**
 * Formats a tooth/area number with appropriate display format.
 * - Pediatric teeth (51-85): Returns letters A-T
 * - Adult quadrants (101-104): Returns initials UR, UL, LL, LR
 * - Pediatric quadrants (201-204): Returns initials UR, UL, LL, LR
 * - Other teeth: Uses standard formatToothOrArea function
 *
 * @param tooth - The tooth number to format
 * @returns Formatted string
 *
 * @example
 * formatToothOrAreaWithInitials(51) → "A"
 * formatToothOrAreaWithInitials(101) → "UR"
 * formatToothOrAreaWithInitials(201) → "UR"
 * formatToothOrAreaWithInitials(3) → "#3"
 */
export const formatToothOrAreaWithInitials = (tooth: number): string => {
  // Handle pediatric teeth (51-85) - return letters A-T
  if (tooth >= 51 && tooth <= 85) {
    return NUMBER_TO_LETTER[tooth] ?? `#${tooth}`;
  }

  // Handle quadrants (adult: 101-104, pediatric: 201-204) - return initials UR, UL, LL, LR
  if ((tooth >= 101 && tooth <= 104) || (tooth >= 201 && tooth <= 204)) {
    const quadrant = quadrantAttributes[tooth as Quadrant | PediatricQuadrant];
    return quadrant?.initials ?? formatToothOrArea(tooth);
  }

  // For all other teeth, use standard formatting
  return formatToothOrArea(tooth);
};

/** Valid pediatric tooth letters (A-T) for verification input */
const PEDIATRIC_LETTERS = new Set(
  Object.keys(LETTER_TO_NUMBER).filter(k => k.length === 1 && k >= 'A' && k <= 'T')
);

/** Valid quadrant initials (UR, UL, LL, LR) for verification input */
const QUADRANT_INITIALS = new Set(['UR', 'UL', 'LL', 'LR']);

/**
 * Formats a reason group's selectedTeeth (FDI numbers) as a comma-separated
 * string suitable for the edit form's text input.
 * - Adult teeth (1-32): kept as plain numbers
 * - Pediatric teeth (51-85): converted to letters A-T
 * - Pediatric quadrants (201-204): converted to UR/UL/LL/LR
 * - Other values (e.g. adult quadrants 101-104): kept as plain numbers
 */
export function formatSelectedTeethForInput(selectedTeeth: number[]): string {
  return selectedTeeth
    .map(tooth => {
      if (isPediatricTooth(tooth)) {
        const letter = numberToLetter(tooth);
        return letter || String(tooth);
      }
      return String(tooth);
    })
    .join(', ');
}

/**
 * Parses user-typed tooth verification input into adult numbers (1-32),
 * pediatric letters (A-T) and quadrant initials (UR/UL/LL/LR).
 * Splits by comma and/or whitespace; strips leading #; ignores invalid tokens.
 */
export function parseToothVerificationInput(input: string): {
  adult: number[];
  pediatric: string[];
  quadrants: string[];
} {
  const adult: number[] = [];
  const pediatric: string[] = [];
  const quadrants: string[] = [];
  const seenAdult = new Set<number>();
  const seenPediatric = new Set<string>();
  const seenQuadrants = new Set<string>();

  const tokens = input
    .split(/[\s,]+/)
    .map(t => t.replace(/^#+/, '').trim())
    .filter(Boolean);

  for (const token of tokens) {
    const upper = token.toUpperCase();
    if (token.length === 1 && PEDIATRIC_LETTERS.has(upper)) {
      if (!seenPediatric.has(upper)) {
        seenPediatric.add(upper);
        pediatric.push(upper);
      }
    } else if (QUADRANT_INITIALS.has(upper)) {
      if (!seenQuadrants.has(upper)) {
        seenQuadrants.add(upper);
        quadrants.push(upper);
      }
    } else {
      const num = parseInt(token, 10);
      if (Number.isInteger(num) && num >= 1 && num <= 32) {
        if (!seenAdult.has(num)) {
          seenAdult.add(num);
          adult.push(num);
        }
      }
    }
  }

  return { adult, pediatric, quadrants };
}

/**
 * Returns true if the parsed verification input matches the group's selected teeth.
 * Compares three sets: adult teeth (1-32), pediatric letters (A-T) and quadrant
 * initials. Quadrants can appear as adult-chart numbers (101-104) or
 * pediatric-chart numbers (201-204) in selectedTeeth, or as initials in
 * selectedPediatricTeeth; all of these display as the same initials, so they
 * are matched as one set — typing the initial once covers a quadrant selected
 * on any chart, and dedups the normal double-write case (e.g. 204 in
 * selectedTeeth alongside 'LR' in selectedPediatricTeeth).
 */
export function toothVerificationMatchesGroup(
  parsed: { adult: number[]; pediatric: string[]; quadrants: string[] },
  group: { selectedTeeth: number[]; selectedPediatricTeeth: string[] }
): boolean {
  const expectedAdult = group.selectedTeeth.filter(t => t >= 1 && t <= 32).sort((a, b) => a - b);
  const upperPediatric = group.selectedPediatricTeeth.map(l => l.toUpperCase());
  const expectedPediatric = upperPediatric.filter(l => PEDIATRIC_LETTERS.has(l)).sort();
  const expectedQuadrants = new Set<string>([
    ...group.selectedTeeth
      .filter(t => (t >= 101 && t <= 104) || (t >= 201 && t <= 204))
      .map(t => quadrantAttributes[t as Quadrant | PediatricQuadrant]?.initials ?? '')
      .filter(Boolean),
    ...upperPediatric.filter(l => QUADRANT_INITIALS.has(l)),
  ]);

  const gotAdult = [...parsed.adult].sort((a, b) => a - b);
  const gotPediatric = [...parsed.pediatric].sort();
  const gotQuadrants = new Set(parsed.quadrants);

  if (
    gotAdult.length !== expectedAdult.length ||
    gotPediatric.length !== expectedPediatric.length ||
    gotQuadrants.size !== expectedQuadrants.size
  ) {
    return false;
  }
  return (
    gotAdult.every((n, i) => n === expectedAdult[i]) &&
    gotPediatric.every((l, i) => l === expectedPediatric[i]) &&
    [...gotQuadrants].every(q => expectedQuadrants.has(q))
  );
}

/**
 * Returns the verification problem for a reason group's re-entered text, or
 * null when nothing needs verifying (no teeth selected) or the input matches.
 * Single source of truth for the field's inline error, the accordion
 * auto-expand, and the route's step validation.
 */
export function getToothVerificationProblem(group: {
  selectedTeeth: number[];
  selectedPediatricTeeth: string[];
  toothVerificationText?: string | null;
}): 'empty' | 'mismatch' | null {
  const hasTeeth = group.selectedTeeth.length > 0 || group.selectedPediatricTeeth.length > 0;
  if (!hasTeeth) return null;
  const trimmed = (group.toothVerificationText ?? '').trim();
  if (!trimmed) return 'empty';
  return toothVerificationMatchesGroup(parseToothVerificationInput(trimmed), group)
    ? null
    : 'mismatch';
}
